import React, { Suspense, lazy, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import BrowserCheck from "./components/BrowserCheck";
import ErrorBoundary from "./components/ErrorBoundary";
import { OfflineBanner } from "./components/OfflineBanner";
import { Loader2, RefreshCw } from "lucide-react";

// Statische Imports nur für Critical Path (Landing / erste Paint)
// Neue User auf der Landingpage laden so nicht die ganze App (kein Layout, kein Login-Bundle).
import Landing from "./pages/Landing";

// --- LAZY IMPORTS (Code Splitting) ---
// Schwere Seiten und B2B/Hire: erst bei Navigation laden (Radix/Charts etc. in separaten Chunks).
const Login = lazy(() => import("./pages/Login"));
const Layout = lazy(() => import("./pages/Layout"));

const B2BLanding = lazy(() => import("./pages/b2b/B2BLanding"));
const RecruitingDashboard = lazy(() => import("./pages/b2b/RecruitingDashboard"));
const HireLogin = lazy(() => import("./pages/b2b/HireLogin"));
const HireDashboardPreview = lazy(() => import("./pages/b2b/HireDashboardPreview"));
const HireInvitesList = lazy(() => import("./pages/hire/HireInvitesList"));
const HireNewInvite = lazy(() => import("./pages/hire/HireNewInvite"));
const HireInviteDetail = lazy(() => import("./pages/hire/HireInviteDetail"));
const HireInviteCalls = lazy(() => import("./pages/hire/HireInviteCalls"));
const HireCallDetail = lazy(() => import("./pages/hire/HireCallDetail"));
const HireSettings = lazy(() => import("./pages/hire/HireSettings"));
const HireTokenShop = lazy(() => import("./pages/hire/HireTokenShop"));
const HireCandidateComparison = lazy(() => import("./pages/hire/HireCandidateComparison"));
const HireAuditLog = lazy(() => import("./pages/hire/HireAuditLog"));
const HirePersonaEditor = lazy(() => import("./pages/hire/HirePersonaEditor"));
const Assess = lazy(() => import("./pages/hire/Assess"));

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Results = lazy(() => import("./pages/Results"));
const DetailedBreakdown = lazy(() => import("./pages/DetailedBreakdown"));
const Progress = lazy(() => import("./pages/Progress"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Features
const Performance = lazy(() => import("./pages/Performance"));
const Call = lazy(() => import("./pages/Call")); // Unsere Hauptseite für Calls
// BetaCall wurde entfernt, da wir jetzt die Live-Funktion integrieren.

const Premium = lazy(() => import("./pages/Premium"));
const Team = lazy(() => import("./pages/Team"));
const Academy = lazy(() => import("./pages/Academy"));
const Community = lazy(() => import("./pages/Community"));
const AudioLibrary = lazy(() => import("./pages/AudioLibrary"));
const Account = lazy(() => import("./pages/Account"));
const Achievements = lazy(() => import("./pages/Achievements"));
const B2B = lazy(() => import("./pages/B2B"));
const CallOfTheDay = lazy(() => import("./pages/CallOfTheDay"));
const Battle = lazy(() => import("./pages/Battle"));
const ScriptGenerator = lazy(() => import("./pages/ScriptGenerator"));
const ObjectionSimulator = lazy(() => import("./pages/ObjectionSimulator"));
const RealCallAnalysis = lazy(() => import("./pages/RealCallAnalysis"));
const CallAnalysisPage = lazy(() => import("./pages/CallAnalysisPage"));
const CallComparisonPage = lazy(() => import("./pages/CallComparisonPage"));
const PostCallScreen = lazy(() => import("./pages/PostCallScreen"));
const ShareClip = lazy(() => import("./pages/ShareClip"));

// --- LADE-KOMPONENTE ---
const PageLoader = () => (
  <div className="h-full w-full flex flex-col items-center justify-center min-h-[50vh] animate-in fade-in duration-300">
    <Loader2 className="h-10 w-10 animate-spin text-indigo-500 mb-4" />
    <p className="text-slate-400 text-sm font-medium">Lade Modul...</p>
  </div>
);

// Interface für Wrapper Props
interface WrapperProps {
  children: React.ReactNode;
}

// Wrapper für Lazy Components
const SuspenseWrapper = ({ children }: WrapperProps) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

/** Zeigt "Authentifizierung..." mit Timeout; nach Ablauf "Erneut versuchen" (v. a. für Mobile). */
function AuthPendingScreen({ onRetry }: { onRetry: () => void }) {
  const [timedOut, setTimedOut] = useState(false);
  const AUTH_TIMEOUT_MS = 8000;

  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), AUTH_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);

  if (timedOut) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
        <p className="text-slate-600 font-medium text-center mb-2">Anmeldung dauert zu lange</p>
        <p className="text-slate-500 text-sm text-center mb-6 max-w-sm">
          Auf dem Handy kann die Weiterleitung manchmal hängen. Bitte erneut versuchen oder in einem
          anderen Browser anmelden.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-4" />
      <p className="text-slate-600 font-medium">Authentifizierung...</p>
    </div>
  );
}

function RootRedirect() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const hasAuthHash =
    typeof window !== "undefined" && window.location.hash.includes("access_token");

  const handleAuthRetry = () => {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    navigate("/login", { replace: true });
  };

  if (!session && hasAuthHash) {
    return <AuthPendingScreen onRetry={handleAuthRetry} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-4" />
      </div>
    );
  }

  if (session) {
    return <Navigate to="/dashboard" replace />;
  } else {
    return <Navigate to="/landing" replace />;
  }
}

function ProtectedRoute({ children }: WrapperProps) {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const hasAuthHash =
    typeof window !== "undefined" && window.location.hash.includes("access_token");

  const handleAuthRetry = () => {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    navigate("/login", { replace: true });
  };

  if (!session && hasAuthHash) {
    return <AuthPendingScreen onRetry={handleAuthRetry} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-4" />
        <div className="text-gray-900 font-medium">Lade Benutzerdaten...</div>
      </div>
    );
  }

  if (!session) return <Navigate to="/landing" replace />;

  return <>{children}</>;
}

/** Wie ProtectedRoute, aber für hire.*: Redirect bei fehlender Session zu "/" (Recruiter-Login). */
function HireProtectedRoute({ children }: WrapperProps) {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const hasAuthHash =
    typeof window !== "undefined" && window.location.hash.includes("access_token");

  const handleAuthRetry = () => {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    navigate("/", { replace: true });
  };

  if (!session && hasAuthHash) {
    return <AuthPendingScreen onRetry={handleAuthRetry} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
        <div className="text-slate-400 font-medium">Lade...</div>
      </div>
    );
  }

  if (!session) return <Navigate to="/" replace />;

  return <>{children}</>;
}

export default function App() {
  // 1. WEICHE FÜR HIRE-SUBDOMAIN (Recruiter + Bewerber Assessment)
  const isHireSubdomain = window.location.hostname.startsWith("hire.");

  if (isHireSubdomain) {
    return (
      <ErrorBoundary>
        <AuthProvider skipBetaCheck logoutRedirectTo="/">
          <Router>
            <Routes>
              <Route
                path="/"
                element={
                  <SuspenseWrapper>
                    <HireLogin />
                  </SuspenseWrapper>
                }
              />
              <Route
                path="/preview"
                element={
                  <SuspenseWrapper>
                    <HireDashboardPreview />
                  </SuspenseWrapper>
                }
              />
              <Route
                path="/assess"
                element={
                  <SuspenseWrapper>
                    <Assess />
                  </SuspenseWrapper>
                }
              />
              <Route
                path="/invites"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireInvitesList />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/invites/new"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireNewInvite />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/invites/:id"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireInviteDetail />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/invites/:id/calls"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireInviteCalls />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/calls/:callId"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireCallDetail />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/compare"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireCandidateComparison />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireSettings />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/tokens"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireTokenShop />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/audit-log"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HireAuditLog />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route
                path="/personas"
                element={
                  <HireProtectedRoute>
                    <SuspenseWrapper>
                      <HirePersonaEditor />
                    </SuspenseWrapper>
                  </HireProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  // 2. WEICHE FÜR B2B SUBDOMAIN
  const isB2B = window.location.hostname.startsWith("b2b.");

  if (isB2B) {
    return (
      <ErrorBoundary>
        <Router>
          <Routes>
            {/* Startseite für Firmen */}
            <Route
              path="/"
              element={
                <SuspenseWrapper>
                  <B2BLanding />
                </SuspenseWrapper>
              }
            />

            {/* Platzhalter für B2B-Login (später eigene Komponente) */}
            <Route
              path="/login"
              element={
                <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
                  B2B Login (Coming Soon)
                </div>
              }
            />

            {/* Das echte Recruiting Dashboard */}
            <Route
              path="/dashboard"
              element={
                <SuspenseWrapper>
                  <RecruitingDashboard />
                </SuspenseWrapper>
              }
            />

            {/* DER GAUNTLET (Einstieg für Kandidaten) */}
            <Route
              path="/c/:campaignId"
              element={
                <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
                  Candidate Start Screen (Coming Soon)
                </div>
              }
            />

            {/* Alles andere zur Landingpage */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ErrorBoundary>
    );
  }

  // 3. NORMALE APP (B2C)
  return (
    <ErrorBoundary>
      <>
        <OfflineBanner />
        <BrowserCheck />

        <AuthProvider>
          <Router>
            <Routes>
              {/* Startseiten-Weiche */}
              <Route path="/" element={<RootRedirect />} />

              {/* Öffentliche Seiten: Landing statisch (schneller First Paint), Login lazy (Radix erst bei Bedarf) */}
              <Route
                path="/login"
                element={
                  <SuspenseWrapper>
                    <Login />
                  </SuspenseWrapper>
                }
              />
              <Route path="/landing" element={<Landing />} />

              {/* === B2B ROUTE (Manuell erreichbar) === */}
              <Route
                path="/recruiting"
                element={
                  <ProtectedRoute>
                    <SuspenseWrapper>
                      <RecruitingDashboard />
                    </SuspenseWrapper>
                  </ProtectedRoute>
                }
              />

              {/* Interne Seiten: Layout + Seiten lazy (Dashboard/Call/Radix in eigenen Chunks) */}
              <Route
                element={
                  <SuspenseWrapper>
                    <Layout />
                  </SuspenseWrapper>
                }
              >
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Dashboard />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/performance"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Performance />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />

                {/* --- CALL HUB (Die zentrale Seite) --- */}
                <Route
                  path="/call"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Call />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />

                {/* --- FEATURES --- */}
                <Route
                  path="/objectionsimulator"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <ObjectionSimulator />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/objections"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <ObjectionSimulator />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/scriptgenerator"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <ScriptGenerator />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/script-generator"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <ScriptGenerator />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/realcallanalysis"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <RealCallAnalysis />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/real-analysis"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <RealCallAnalysis />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />

                {/* Restliche Routen */}
                <Route
                  path="/call-of-the-day"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <CallOfTheDay />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/battle"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Battle />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/results"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Results />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/results/:id"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <DetailedBreakdown />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analysis/:id"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <CallAnalysisPage />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/compare"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <CallComparisonPage />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/post-call/:callId"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <PostCallScreen />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/progress"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Progress />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/premium"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Premium />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/team"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Team />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/academy"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Academy />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/community"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Community />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/library"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <AudioLibrary />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Account />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/achievements"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <Achievements />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/b2b"
                  element={
                    <ProtectedRoute>
                      <SuspenseWrapper>
                        <B2B />
                      </SuspenseWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/share/:clipId"
                  element={
                    <SuspenseWrapper>
                      <ShareClip />
                    </SuspenseWrapper>
                  }
                />

                <Route
                  path="*"
                  element={
                    <SuspenseWrapper>
                      <NotFound />
                    </SuspenseWrapper>
                  }
                />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </>
    </ErrorBoundary>
  );
}
